import { useState, useRef, useCallback } from 'react';
import { posthog } from '@/utils/posthog';

interface PullToUnlockProps {
  chapter: {
    _id: string;
    sequence: number;
    title: string;
    tokenCost: number;
  };
  onUnlock: () => void;
  isUnlocked: boolean;
  teaserText?: string;
  bookId?: string;
  bookTitle?: string;
  externalPullProgress?: number;
}

export function PullToUnlock({ 
  chapter, 
  onUnlock, 
  isUnlocked,
  teaserText,
  bookId,
  bookTitle,
  externalPullProgress
}: PullToUnlockProps) {
  const [internalPullProgress, setInternalPullProgress] = useState(0);
  const [isPulling, setIsPulling] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const startY = useRef(0);
  
  // Use external progress if provided (for wheel scroll), otherwise use internal (for touch)
  const pullProgress = externalPullProgress !== undefined ? externalPullProgress : internalPullProgress;

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    startY.current = e.touches[0].clientY;
    setIsPulling(true);
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isPulling) return;
    const currentY = e.touches[0].clientY;
    const diff = startY.current - currentY;
    const progress = Math.min(Math.max(diff / 150, 0), 1);
    setInternalPullProgress(progress);
  }, [isPulling]);

  const handleTouchEnd = useCallback(() => {
    if (internalPullProgress >= 0.8) {
      onUnlock();
      // Track: Pull-to-unlock gesture completed
      posthog.capture('pull_to_unlock_completed', {
        book_id: bookId,
        book_title: bookTitle,
        chapter_id: chapter._id,
        chapter_sequence: chapter.sequence,
        chapter_title: chapter.title,
        token_cost: chapter.tokenCost,
      });
    }
    setInternalPullProgress(0);
    setIsPulling(false);
  }, [internalPullProgress, onUnlock, bookId, bookTitle, chapter]);

  if (isUnlocked) return null;

  return (
    <div 
      ref={containerRef}
      className="relative py-16 px-6 text-center select-none"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Teaser Text */}
      <div 
        className="mb-8 transition-opacity duration-300"
        style={{ opacity: 0.3 + pullProgress * 0.7 }}
      >
        <p className="font-body text-lg text-[var(--text-secondary)] italic leading-relaxed max-w-md mx-auto">
          {teaserText || "The story continues..."}
        </p>
      </div>

      {/* Pull Indicator */}
      <div className="flex flex-col items-center gap-4">
        <div className="relative">
          {/* Progress Ring */}
          <svg className="w-16 h-16 -rotate-90" viewBox="0 0 36 36">
            <circle
              cx="18"
              cy="18"
              r="16"
              fill="none"
              stroke="var(--bg-tertiary)"
              strokeWidth="2"
            />
            <circle
              cx="18"
              cy="18"
              r="16"
              fill="none"
              stroke="var(--accent)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeDasharray={`${pullProgress * 100} 100`}
              className="transition-all duration-100"
            />
          </svg>
          
          {/* Icon */}
          <div className="absolute inset-0 flex items-center justify-center">
            <svg 
              className="w-6 h-6 text-[var(--accent)] transition-transform duration-200"
              style={{ transform: `translateY(${-pullProgress * 4}px)` }}
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 10l7-7m0 0l7 7m-7-7v18" />
            </svg>
          </div>
        </div>

        <p className="font-ui text-sm text-[var(--text-tertiary)]">
          {pullProgress >= 0.8 ? 'Release to unlock' : 'Pull up to continue'}
        </p>
        
        <div className="flex items-center gap-2 text-[var(--accent)]">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2"/>
            <text x="12" y="16" textAnchor="middle" fontSize="8" fontWeight="bold">L</text>
          </svg>
          <span className="font-ui text-xs font-medium">{chapter?.tokenCost || 30} tokens</span>
        </div>
      </div>
    </div>
  );
}
